import { CommonModule } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  Renderer2,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { CdkDragEnd, DragDropModule, DragRef, Point } from '@angular/cdk/drag-drop';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import {
  buildCollabQuickActionRow,
  CollabQuickActionContext,
  CollabQuickActionContextService,
  CollabQuickActionName,
  CollabQuickActionRowComponent,
  CollabQuickActionSurface,
  CollabQuickActionVM,
  MessageInputComponent,
  MessageInputVM,
  MessageVM,
  ReplyOverlayComponent,
  UnsendMessageConfirmDialogComponent,
} from '@libs/collaboration';
import { AssistantBannerService } from '../../assistant-banner.service';
import { ASSISTANT_CHAT_WINDOW_MIN_HEIGHT, ASSISTANT_CHAT_WINDOW_MIN_WIDTH } from '../../assistant-chat-window.constants';
import { AssistantChatWindowService } from '../../assistant-chat-window.service';
import { AssistantLauncherService } from '../../assistant-launcher.service';
import { AssistantMessageInputScopeDirective } from '../../assistant-message-input-scope.directive';
import { AssistantMessagesService } from '../../assistant-messages.service';
import { AssistantMessageListComponent } from '../assistant-message-list';
import { AssistantTrialBadgeComponent } from '../assistant-trial-badge';

const openAnimationTiming = '200ms cubic-bezier(0.4, 0, 0.2, 1)';

export type AssistantChatWindowResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

@Component({
  selector: 'lib-assistant-chat-window',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    TranslateModule,
    SvgIconComponent,
    AssistantTrialBadgeComponent,
    AssistantMessageListComponent,
    CollabQuickActionRowComponent,
    MessageInputComponent,
    AssistantMessageInputScopeDirective,
    ReplyOverlayComponent,
    LottieComponent,
  ],
  templateUrl: './assistant-chat-window.component.html',
  styleUrl: './assistant-chat-window.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('openClose', [
      transition(':enter', [style({ opacity: 0, scale: '95%' }), animate(openAnimationTiming, style({ opacity: 1, scale: '100%' }))]),
    ]),
  ],
})
export class AssistantChatWindowComponent {
  @ViewChild(MessageInputComponent) private readonly messageInputComponent!: MessageInputComponent;

  public readonly siteId = input<string | undefined>();
  public readonly isInsideTrialPeriod = input<boolean>(false);
  // The header of the layout: the window stays below it, because the header covers the window and its drag handle
  public readonly dragTopBoundary = input<HTMLElement | undefined>();

  private readonly assistantChatWindowService = inject(AssistantChatWindowService);
  protected readonly assistantMessagesService = inject(AssistantMessagesService);
  private readonly assistantLauncherService = inject(AssistantLauncherService);
  private readonly assistantBannerService = inject(AssistantBannerService);
  private readonly dialog = inject(Dialog);
  private readonly quickActionContextService = inject(CollabQuickActionContextService);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private dialogRef: DialogRef<boolean> | null = null;

  protected readonly isOpen = this.assistantChatWindowService.isOpen;
  protected readonly messages = this.assistantMessagesService.messages;
  // The welcome message is the first entry of the conversation: it stays above the messages once they exist, but only
  // renders once the oldest page is loaded so it never floats in the middle of older history.
  // It also waits for the banner text, so the default text never shows first and then switches to the custom text.
  protected readonly showWelcome = computed(
    () => this.assistantBannerService.isLoaded() && (this.messages().length === 0 || this.assistantMessagesService.noMoreMessages()),
  );
  protected readonly welcomeBanner = this.assistantBannerService.banner;
  protected readonly isLoading = this.assistantMessagesService.isLoading;
  // FFD Notes has no support center or learning lab to link to
  protected readonly showLinkPills = computed(() => !this.isLoading() && !this.assistantLauncherService.isFfdNotes());
  protected readonly lottieOptions: AnimationOptions = { path: '/assets/animations/loading-spinner.json' };
  // Only shown during the first-time experience; every other way of opening the window dismisses it.

  /**
   * The screen the quick action row is filtered against. It tracks navigation, so the row updates
   * while the assistant stays open (AC-9).
   */
  protected readonly quickActionContext = computed<CollabQuickActionContext>(() => ({
    surface: CollabQuickActionSurface.PersonalAssistant,
    siteId: this.siteId() ?? '',
    screenFeature: this.quickActionContextService.screenFeature(),
    patientId: this.quickActionContextService.patientId(),
  }));

  protected readonly quickActions = computed<CollabQuickActionVM[]>(() =>
    buildCollabQuickActionRow(this.assistantMessagesService.collabQuickActions(), this.quickActionContext()),
  );

  protected onNamedQuickAction(name: CollabQuickActionName): void {
    void this.messageInputComponent?.runNamedQuickAction(name);
  }

  protected readonly replyingToMessage = signal<MessageVM | null>(null);
  protected readonly editingMessageId = signal<string | null>(null);
  protected readonly historyOpenMessageId = signal<string | null>(null);

  // Seeded from the root service, so a remounted window comes back with the size and place of the session
  protected readonly width = signal(this.assistantChatWindowService.geometry().width);
  protected readonly height = signal(this.assistantChatWindowService.geometry().height);
  private readonly translateX = signal(this.assistantChatWindowService.geometry().translateX);
  private readonly translateY = signal(this.assistantChatWindowService.geometry().translateY);
  protected readonly dragPosition = computed(() => ({ x: this.translateX(), y: this.translateY() }));

  private resizeDirection: AssistantChatWindowResizeDirection | null = null;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = 0;
  private resizeStartHeight = 0;
  private resizeStartTranslateX = 0;
  private resizeStartTranslateY = 0;
  private resizeStartTop = 0;
  private removeResizeListeners: (() => void) | null = null;

  constructor() {
    effect(() => {
      const isOpen = this.isOpen();
      // untracked: these read signals (collaboration, messages, marker) that they themselves mutate -
      // without untracked, those writes re-trigger this effect while the window is still open and
      // immediately mark a just-set unread message as read again.
      untracked(() => {
        if (isOpen) {
          this.assistantMessagesService.loadIfNeeded();
          this.assistantMessagesService.markAsReadIfWindowOpen();
          return;
        }
        // Closing ends the reading session, so the divider must not survive into the next open.
        // Covers both the X button and the launcher toggle, which flip the same signal.
        this.assistantMessagesService.clearNewMessageMarker();
      });
    });

    // A window saved under the header (e.g. before the top boundary existed) could never be grabbed again
    effect(() => {
      if (this.isOpen()) {
        afterNextRender(() => this.moveBelowTopBoundary(), { injector: this.injector });
      }
    });

    this.destroyRef.onDestroy(() => this.removeResizeListeners?.());
  }

  // With a constrainPosition function, CDK renders the window with its top-left corner at the returned point
  protected readonly constrainDragPosition = (
    pointer: Point,
    _dragRef: DragRef,
    _dimensions: DOMRect,
    pickupPositionInElement: Point,
  ): Point => ({
    x: pointer.x - pickupPositionInElement.x,
    y: Math.max(pointer.y - pickupPositionInElement.y, this.topBoundary() ?? Number.NEGATIVE_INFINITY),
  });

  protected onResizeStart(event: MouseEvent, direction: AssistantChatWindowResizeDirection): void {
    event.preventDefault();
    event.stopPropagation();

    this.resizeDirection = direction;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.width();
    this.resizeStartHeight = this.height();
    this.resizeStartTranslateX = this.translateX();
    this.resizeStartTranslateY = this.translateY();
    this.resizeStartTop = this.windowTop();

    const removeMouseMove = this.renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onResize(e));
    const removeMouseUp = this.renderer.listen('document', 'mouseup', () => this.onResizeEnd());
    this.removeResizeListeners = (): void => {
      removeMouseMove();
      removeMouseUp();
      this.removeResizeListeners = null;
    };
  }

  protected onDragEnded(event: CdkDragEnd): void {
    const { x, y } = event.source.getFreeDragPosition();
    this.translateX.set(x);
    this.translateY.set(y);
    this.saveGeometry();
  }

  private onResize(event: MouseEvent): void {
    const direction = this.resizeDirection;

    if (!direction) {
      return;
    }

    // Clamping the pointer to the viewport keeps the dragged edge - and with it the window - on screen
    const deltaX = Math.min(Math.max(event.clientX, 0), window.innerWidth) - this.resizeStartX;
    const deltaY = Math.min(Math.max(event.clientY, 0), window.innerHeight) - this.resizeStartY;

    let width = this.resizeStartWidth;
    let height = this.resizeStartHeight;

    if (direction.includes('e')) {
      width = this.resizeStartWidth + deltaX;
    }
    if (direction.includes('w')) {
      width = this.resizeStartWidth - deltaX;
    }
    if (direction.includes('s')) {
      height = this.resizeStartHeight + deltaY;
    }
    if (direction.includes('n')) {
      height = this.resizeStartHeight - deltaY;
    }

    const topBoundary = this.topBoundary();
    if (direction.includes('n') && topBoundary !== undefined) {
      // The top edge stops at the header, the same as when the window is dragged
      height = Math.min(height, this.resizeStartHeight + this.resizeStartTop - topBoundary);
    }

    width = Math.max(ASSISTANT_CHAT_WINDOW_MIN_WIDTH, width);
    height = Math.max(ASSISTANT_CHAT_WINDOW_MIN_HEIGHT, height);

    // The window is CSS-anchored by its bottom-left corner, so a size change grows it to the right and upwards.
    // The 'w' and 's' handles move the anchored edges, which the drag offset compensates for - derived from the
    // clamped size so the opposite edge stays put once the window hits its minimum size.
    const widthChange = direction.includes('w') ? width - this.resizeStartWidth : 0;
    const heightChange = direction.includes('s') ? height - this.resizeStartHeight : 0;

    this.width.set(width);
    this.height.set(height);
    this.translateX.set(this.resizeStartTranslateX - widthChange);
    this.translateY.set(this.resizeStartTranslateY + heightChange);
  }

  private onResizeEnd(): void {
    this.resizeDirection = null;
    this.removeResizeListeners?.();
    this.saveGeometry();
  }

  private moveBelowTopBoundary(): void {
    const topBoundary = this.topBoundary();

    if (topBoundary === undefined) {
      return;
    }

    const overlap = topBoundary - this.windowTop();

    if (overlap > 0) {
      this.translateY.update((translateY) => translateY + overlap);
      this.saveGeometry();
    }
  }

  private topBoundary(): number | undefined {
    return this.dragTopBoundary()?.getBoundingClientRect().bottom;
  }

  // Derived from the host, which is not scaled by the open animation: the host's bottom is the window's CSS anchor
  private windowTop(): number {
    return this.elementRef.nativeElement.getBoundingClientRect().bottom + this.translateY() - this.height();
  }

  private saveGeometry(): void {
    this.assistantChatWindowService.setGeometry({
      width: this.width(),
      height: this.height(),
      translateX: this.translateX(),
      translateY: this.translateY(),
    });
  }

  // Closing the window ends the first-time experience, so the nudge goes with it for good.
  protected onCloseClick(): void {
    this.assistantLauncherService.dismissNudge();
    this.assistantChatWindowService.close();
  }

  protected onSend(message: MessageInputVM): void {
    const replyingTo = this.replyingToMessage();
    const threadMessageId = replyingTo?.parentMessage?.id ?? replyingTo?.id;
    this.assistantMessagesService.sendMessage({ text: message.text, threadMessageId, attachments: message.attachments.allAttachments });
  }

  protected onRecordClick(): void {
    this.assistantMessagesService.toggleScreenRecording(this.siteId());
  }

  protected onRetrySendMessage(messageId: string): void {
    this.assistantMessagesService.retrySendMessage(messageId);
  }

  protected onUnsendMessage(messageId: string): void {
    this.dialogRef = this.dialog.open<boolean>(UnsendMessageConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
    });
    this.dialogRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.assistantMessagesService.unsendMessage(messageId);
      }
    });
  }

  protected onMarkMessageUnread(messageId: string): void {
    this.assistantMessagesService.markMessageAsUnread(messageId);
  }

  protected onStartEdit(messageId: string): void {
    this.editingMessageId.set(messageId);
    this.historyOpenMessageId.set(null);
  }

  protected onToggleHistory(messageId: string): void {
    this.historyOpenMessageId.update((current) => (current === messageId ? null : messageId));
  }

  protected onCancelMessageEdit(): void {
    this.editingMessageId.set(null);
  }

  protected onEditMessage(event: { messageId: string; text: string }): void {
    this.editingMessageId.set(null);
    this.assistantMessagesService.editMessage(event.messageId, event.text);
  }

  protected onReply(message: MessageVM): void {
    this.replyingToMessage.set(message);
    this.historyOpenMessageId.set(null);
    this.openThread(message.parentMessage?.id ?? message.id);
  }

  protected onThreadClick(message: MessageVM): void {
    this.replyingToMessage.set(message);
    this.historyOpenMessageId.set(null);

    if (message.threadId) {
      this.openThread(message.threadId);
    }
  }

  protected onCloseReply(): void {
    this.replyingToMessage.set(null);
    this.historyOpenMessageId.set(null);
    this.assistantMessagesService.clearThreadMessages();
  }

  protected loadOlderThreadMessages(): void {
    this.assistantMessagesService.loadOlderThreadMessages();
  }

  private openThread(threadId: string): void {
    this.assistantMessagesService.openThread(threadId);
    this.messageInputComponent.focus();
  }
}
